import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { CarConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import * as React from "react";
import { setCookie } from "components/utility/cookie";
import { CarLastSearchData, LastSearchData } from "stores/wizard/LastSearchData";
import { Travelers, Room } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { DateState } from "../global/date/typings";
import { dateState } from "../global/date";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { locKeys } from "src/components/flexComponents/WizardCarPWA/components/l10n";
import { carSubLOBs } from "../typings";
export class CarWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  public lastSearchData?: CarLastSearchData;

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return (this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB) && this.isDesktop;
  }

  @observable
  public config: CarConfig;

  @observable
  public isDifferentDropOff: boolean = false;

  public toggleIsDifferentDropOff() {
    const { pickUpLocationLabel, pickUpDropOffLabel } = locKeys;
    this.isDifferentDropOff = !this.isDifferentDropOff;
    this.config.location.origin.labelToken = this.isDifferentDropOff ? pickUpLocationLabel : pickUpDropOffLabel;
  }

  public setIsDifferentDropoff(differentDropoff: boolean) {
    const { pickUpLocationLabel, pickUpDropOffLabel } = locKeys;
    this.isDifferentDropOff = differentDropoff;
    this.config.location.origin.labelToken = this.isDifferentDropOff ? pickUpLocationLabel : pickUpDropOffLabel;
  }

  @observable
  public preferredBrand: string = "";

  @observable
  public pickupTime: string;

  private pickupTimeCannonicalMinutesDefault = 630;
  public pickupTimeCannonicalMinutes: number = this.pickupTimeCannonicalMinutesDefault; //"1030AM"

  @observable
  public returnTime: string;

  @action
  public setDefaultShowingTime = () => {
    this.pickupTime = this.returnTime = this.config.defaultShowingTime;
  };

  public subLOBState: string = "";

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  public driversAgeInputRef: React.RefObject<HTMLInputElement>;

  @observable
  public carCouponCodeType: string = "noCode";

  @observable
  public couponCode0: string;

  @observable
  public couponCode1: string;

  @observable
  public couponCode2: string;

  @observable
  public driversAge = "30";

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public isCarFormValid = true;

  @observable
  public isFromAirportFormValid = true;

  @observable
  public isToAirportFormValid = true;

  @observable
  public numberOfErrors = 0;

  @computed
  public get date() {
    return this.globalState.date;
  }

  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = subLOBState;
  }

  public getFieldName = (field: string) => {
    const fieldName = !this.tabSelected
      ? this.subLOBState === "toAirport"
        ? this.config.gtConfig.toAirportLocation[field]?.inputName
        : this.config.gtConfig.location[field]?.inputName
      : this.config.location[field].inputName;

    return `location-field-${fieldName}`;
  };

  // Ground Transportation
  @observable
  public additionalSingleDate: DateState;

  @observable
  public roundTrip: boolean = false;

  @observable
  public tabSelected: boolean = true;

  @observable
  public nonHotelTravelersInvalidKey: string | "";

  public toggleBookRoundtrip() {
    this.roundTrip = !this.roundTrip;
  }

  public toggleTabSelected() {
    this.tabSelected = !this.tabSelected;
  }

  @action
  public updateAdditionalSingleDate = (start: Date, end: Date) => {
    this.additionalSingleDate.start = start;
    this.additionalSingleDate.end = end;
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers);
    this.nonHotelTravelersInvalidKey = "";
  };

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateLessThanNTravelers = () => {
    if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 1,
        children: [],
        infants: [],
      },
    ];

    const applicableTravelerState = this.globalState.travelers.nonHotel;
    const applicableRoomsForTravelers: Room[] = applicableTravelerState.rooms.map((room: Room) => {
      const applicableAdultsPerRoom =
        room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;
      const combinedChildren = room.children.concat(room.infants);
      const applicableChildrenPerRoom =
        combinedChildren.length > this.config.travelers.maxChildrenPerRoom
          ? combinedChildren.splice(0, this.config.travelers.maxChildrenPerRoom)
          : combinedChildren;

      return {
        adults: applicableAdultsPerRoom,
        children: applicableChildrenPerRoom,
        infants: [],
      };
    });

    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const gtTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
    };

    return gtTravelers;
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  //validations
  @observable
  public originInvalidKey: string | "";

  @observable
  public destinationInvalidKey: string | "";

  @observable
  public driversAgeInvalidKey: string | "";

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @action
  public validateOriginField = () => {
    if (!this.globalState.validateOriginField()) {
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;
      const fieldName = this.getFieldName("origin");
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement(fieldName)!;

      return false;
    }

    this.originInvalidKey = "";

    return true;
  };

  public findElementRef = (wizardInputsArray: React.RefObject<HTMLInputElement>[], elementName: string) =>
    this.wizardInputsArray.find(
      (element) => element.current !== null && element.current.outerHTML.includes(elementName)
    );

  @action
  public validateDestinationField = () => {
    if (this.isDifferentDropOff || this.subLOBState === "fromAirport" || this.subLOBState === "toAirport") {
      if (!this.globalState.validateDestinationField()) {
        this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;

        if (!this.errorInputRef) {
          this.errorInputRef = this.findFocusElement("destination")!;
        }

        return false;
      }

      return true;
    }

    return true;
  };

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @observable
  public timeStartInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    const { start, end } = this.date;
    const { maxDaysRange: gtMaxDaysRange, invalidMaxDatesRangeMessageToken } = this.config.gtConfig.date;
    const { maxDaysRange: carMaxDaysRange } = this.config.date;

    if (!this.tabSelected && !this.globalState.validateMaxDateRange(start, end, gtMaxDaysRange)) {
      this.dateEndInvalidKey = invalidMaxDatesRangeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    if (!this.globalState.validateMaxDateRange(start, end, carMaxDaysRange)) {
      this.dateEndInvalidKey = invalidMaxDatesRangeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateStartTime = () => {
    const isToday = (dt: Date) => {
      const today = new Date();

      return (
        dt.getDate() === today.getDate() &&
        dt.getMonth() === today.getMonth() &&
        dt.getFullYear() === today.getFullYear()
      );
    };

    const isSelectedHourValid = () => {
      const today = new Date();
      const now = today.getHours() * 60 + today.getMinutes();

      return this.pickupTimeCannonicalMinutes >= now;
    };

    const isCarTabSelected = this.tabSelected;

    if (isToday(this.date.start) && !isSelectedHourValid()) {
      this.timeStartInvalidKey = isCarTabSelected
        ? this.config.date.invalidStartTimeMessageToken!
        : this.config.gtConfig.date.invalidStartTimeMessageToken!;

      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateStartTimeField = () => this.validateStartTime();

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public validateDriversAgeField = () => {
    if (!this.driversAge) {
      this.driversAgeInvalidKey = this.config.invalidDriversAgeMessageToken;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("age")!;
      }

      return false;
    }

    return true;
  };

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.timeStartInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
    this.driversAgeInvalidKey = "";
  };

  public validateForm = () => {
    this.cleanErrorState();

    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationField(),
      this.validateEndDateField(),
      this.validateStartTimeField(),
      this.validateDriversAgeField(),
    ];

    if (!this.tabSelected) {
      validations.push(this.validateTravelersField());
    }
    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.timeStartInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  @action
  public updateCouponCodeType = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    const carCouponCodeType = selectEvent.target.value;
    this.carCouponCodeType = carCouponCodeType;
    this.couponCode0 = "";
    this.couponCode1 = "";
    this.couponCode2 = "";
  };

  @action
  public updateCouponCode = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    if (index === 0) {
      this.couponCode0 = code;
    } else if (index === 1) {
      this.couponCode1 = code;
    } else if (index === 2) {
      this.couponCode2 = code;
    }
  };

  @action
  public updateDriversAgeInput = (event: React.ChangeEvent<HTMLInputElement>) => (this.driversAge = event.target.value);

  /* istanbul ignore next */
  public updateHotelRegionId = (regionId: number) => {
    if (this.subLOBState === "toAirport") {
      // @ts-ignore
      this.config.gtConfig.toAirportLocation?.origin?.essAdapterConfig!.regionId = regionId;
    } else {
      // @ts-ignore
      this.config.gtConfig.location?.destination?.essAdapterConfig!.regionId = regionId;
    }
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateOriginSelection(selection);
    if (!this.tabSelected) {
      const {
        data: { regionId },
      } = selection as TypeaheadSelectionProps;
      this.updateHotelRegionId(parseInt(regionId, 10));
      this.originInvalidKey = "";

      return;
    }
    if (!this.globalState.location.destination.value) {
      this.globalState.updateDestinationSelection(selection);
    }
    this.originInvalidKey = "";
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla);
  }

  @action
  public updateOriginFromCookie(value: string, regionID: string, countryCode: string, latLong: string, ttla: string) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla);
    this.originInvalidKey = "";
  }

  @action
  public setPreferredBrand = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.preferredBrand = selectEvent.target.value;
    this.couponCode0 = "";
    this.couponCode1 = "";
    this.couponCode2 = "";
  };

  @action
  public setPickupTime = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.pickupTime = selectEvent.target.value;
    const cannonicalMinutes =
      selectEvent.target && selectEvent.target.selectedOptions
        ? selectEvent.target.selectedOptions[0].dataset["time"]
        : "";
    this.pickupTimeCannonicalMinutes = cannonicalMinutes
      ? parseInt(cannonicalMinutes, 10)
      : this.pickupTimeCannonicalMinutesDefault;
    this.timeStartInvalidKey = "";
  };

  @action
  public setReturnTime = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    this.returnTime = selectEvent.target.value;
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const origin = this.location.origin;
      const originMetaData = this.location.origin.metaData;
      const destination = this.location.destination;
      const detinationMetaData = this.location.destination.metaData;
      const date = this.date;
      const couponCode0 = this.couponCode0;
      const couponCode1 = this.couponCode1;
      const couponCode2 = this.couponCode2;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        car: {
          origin: {
            value: origin.value ? encodeURIComponent(origin.value) : "",
            regionID: originMetaData.regionId ? encodeURIComponent(originMetaData.regionId) : "",
            ttla: originMetaData.ttla ? encodeURIComponent(originMetaData.ttla) : "",
            countryCode: originMetaData.countryCode ? encodeURIComponent(originMetaData.countryCode) : "",
            latLong: originMetaData.latLong ? encodeURIComponent(originMetaData.latLong) : "",
          },
          destination: {
            value: destination.value ? encodeURIComponent(destination.value) : "",
            regionID: detinationMetaData.regionId ? encodeURIComponent(detinationMetaData.regionId) : "",
            ttla: detinationMetaData.ttla ? encodeURIComponent(detinationMetaData.ttla) : "",
            countryCode: detinationMetaData.countryCode ? encodeURIComponent(detinationMetaData.countryCode) : "",
            latLong: detinationMetaData.latLong ? encodeURIComponent(detinationMetaData.latLong) : "",
          },
          date: {
            pickup: date.start ? date.start.toString() : "",
            dropoff: date.end ? date.end.toString() : "",
          },
          time: {
            pickup: this.pickupTime,
            return: this.returnTime,
          },
          isDifferentDropoff: this.isDifferentDropOff.toString(),
          preferred_brand: this.preferredBrand,
          coupon_code: {
            type: this.carCouponCodeType,
            0: couponCode0 ? encodeURIComponent(couponCode0) : "",
            1: couponCode1 ? encodeURIComponent(couponCode1) : "",
            2: couponCode2 ? encodeURIComponent(couponCode2) : "",
          },
        },
      };

      if (this.isDifferentDropOff) {
        lastSearchCookieObject.car!.destination.value = destination.value ? encodeURIComponent(destination.value) : "";
        lastSearchCookieObject.car!.destination.regionID = detinationMetaData.regionId
          ? encodeURIComponent(detinationMetaData.regionId)
          : "";
        lastSearchCookieObject.car!.destination.ttla = detinationMetaData.ttla
          ? encodeURIComponent(detinationMetaData.ttla)
          : "";
        lastSearchCookieObject.car!.destination.countryCode = detinationMetaData.countryCode
          ? encodeURIComponent(detinationMetaData.countryCode)
          : "";
        lastSearchCookieObject.car!.destination.latLong = detinationMetaData.latLong
          ? encodeURIComponent(detinationMetaData.latLong)
          : "";
      }

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
      });
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;
    this.setDefaultShowingTime();

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const originValue = decodeURIComponent(lastSearchData.origin?.value);
      const destinationValue = decodeURIComponent(lastSearchData.destination?.value);
      const startDateFromCookie = lastSearchData.date?.pickup ? new Date(lastSearchData.date.pickup) : new Date();
      const endDateFromCookie = lastSearchData.date?.dropoff ? new Date(lastSearchData.date.dropoff) : new Date();

      const isDifferentDropoff = lastSearchData.isDifferentDropoff
        ? JSON.parse(lastSearchData.isDifferentDropoff)
        : undefined;
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      this.updateOriginFromCookie(
        originValue !== "undefined" ? originValue : "",
        lastSearchData.origin?.regionID ? lastSearchData.origin.regionID : "",
        lastSearchData.origin?.countryCode ? lastSearchData.origin.countryCode : "",
        lastSearchData.origin?.latLong ? lastSearchData.origin.latLong : "",
        lastSearchData.origin?.ttla ? lastSearchData.origin.ttla : ""
      );

      if (isDifferentDropoff && destinationValue) {
        this.setIsDifferentDropoff(isDifferentDropoff);
        if (originValue !== "undefined" && destinationValue === "undefined") {
          lastSearchData.destination = {
            value: originValue,
            regionID: lastSearchData.origin.regionID,
            countryCode: lastSearchData.origin.countryCode,
            latLong: lastSearchData.origin.latLong,
            ttla: lastSearchData.origin.ttla,
          };
          this.updateDestinationFromCookie(
            originValue,
            lastSearchData.destination.regionID,
            lastSearchData.destination.countryCode,
            lastSearchData.destination.latLong,
            lastSearchData.destination.ttla
          );
        } else {
          this.updateDestinationFromCookie(
            destinationValue !== "undefined" ? destinationValue : "",
            lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
            lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
            lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
            lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : ""
          );
        }
      }
      this.updateDateSelection(startDateToUse, endDateToUse);

      //populate from last search or from default if unavailable
      this.pickupTime = lastSearchData.time?.pickup ? lastSearchData.time.pickup : this.pickupTime;
      this.returnTime = lastSearchData.time?.return ? lastSearchData.time.return : this.returnTime;
      this.preferredBrand = lastSearchData.preferred_brand ? lastSearchData.preferred_brand : this.preferredBrand;
      this.carCouponCodeType = lastSearchData.coupon_code?.type
        ? lastSearchData.coupon_code.type
        : this.carCouponCodeType;
      this.couponCode0 = lastSearchData.coupon_code?.hasOwnProperty("0")
        ? lastSearchData.coupon_code["0"]
        : this.couponCode0;
      this.couponCode1 = lastSearchData.coupon_code?.hasOwnProperty("1")
        ? lastSearchData.coupon_code["1"]
        : this.couponCode1;
      this.couponCode2 = lastSearchData.coupon_code?.hasOwnProperty("2")
        ? lastSearchData.coupon_code["2"]
        : this.couponCode2;
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.updateInvalidFormsState(false);
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
      this.updateInvalidFormsState(true);
    }
  };

  /**
   * Cleans error state in all forms
   */

  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
    this.isCarFormValid = true;
    this.isFromAirportFormValid = true;
    this.isToAirportFormValid = true;
  };

  @action
  public resetSubLobState = () => {
    this.updateSubLOBState(carSubLOBs.rental);
  };

  @action
  public resetSubLobTabs = () => {
    this.tabSelected = true;
  };

  //Resets other cars forms to not have errors when you change to a different car sublob type
  //Shows error message in the right cars type
  @action
  public updateInvalidFormsState = (validState?: boolean) => {
    const state = validState !== undefined ? validState : true;
    switch (this.subLOBState) {
      case carSubLOBs.from:
        this.isFromAirportFormValid = state;
        this.isCarFormValid = true;
        this.isToAirportFormValid = true;
        break;
      case carSubLOBs.to:
        this.isToAirportFormValid = state;
        this.isFromAirportFormValid = true;
        this.isCarFormValid = true;
        break;
      default:
        this.isCarFormValid = state;
        this.isToAirportFormValid = true;
        this.isFromAirportFormValid = true;
        break;
    }
  };

  constructor(config: CarConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    this.additionalSingleDate = dateState();
    this.setDefaultShowingTime();
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.car;
    }
  }
}
